// /client/src/components/SetPassword.js
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const SetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Support both flows: userId (email verification) or token (password reset)
  const userId = searchParams.get('userId');
  const token = searchParams.get('token');
  const mode = searchParams.get('mode'); // 'reset' for password reset flow

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId && !token) {
      setErrors({ submit: 'Invalid request. Missing required parameters.' });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let response;
      
      if (token && mode === 'reset') {
        // Password reset flow
        response = await fetch('/api/contacts/reset-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token,
            password: formData.password,
            confirmPassword: formData.confirmPassword
          })
        });
      } else {
        // Email verification flow (existing)
        response = await fetch('/api/contacts/set-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            password: formData.password,
            confirmPassword: formData.confirmPassword
          })
        });
      }

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login', {
            state: { successMessage: 'Password set successfully! You can now log in.' }
          });
        }, 3000);
      } else {
        setErrors({ submit: data.error || 'Failed to set password. Please try again.' });
      }
    } catch (error) {
      console.error('Error setting password:', error);
      setErrors({ submit: 'Network error. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  const isResetMode = token && mode === 'reset';
  const title = isResetMode ? 'Reset Your Password' : 'Set Your Password';

  if (success) {
    return (
      <section className="hero is-fullheight theme-light" data-theme="light">
        <div className="hero-body">
          <div className="container">
            <div className="columns is-centered">
              <div className="column is-5-tablet is-4-desktop">
                <div className="box">
                  <div className="notification is-success is-light">
                    <p className="has-text-centered">
                      <span className="icon is-large">
                        <i className="fas fa-check-circle fa-2x"></i>
                      </span>
                    </p>
                    <p className="has-text-centered mt-3">
                      <strong>Password set successfully!</strong>
                    </p>
                    <p className="has-text-centered mt-2">
                      Redirecting to login page...
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="hero is-fullheight theme-light" data-theme="light">
      <div className="hero-body">
        <div className="container">
          <div className="columns is-centered">
            <div className="column is-5-tablet is-4-desktop">
              <div className="box">
                <div className="has-text-centered mb-5">
                  <h1 className="title is-3">{title}</h1>
                  <p className="subtitle is-6">Create a secure password for your account</p>
                </div>

                <form onSubmit={handleSubmit}>
                  {errors.submit && (
                    <div className="notification is-danger is-light">
                      <button 
                        className="delete" 
                        onClick={() => setErrors(prev => ({ ...prev, submit: '' }))}
                      ></button>
                      {errors.submit}
                    </div>
                  )}

                  <div className="field">
                    <label className="label">Password</label>
                    <div className="control has-icons-left">
                      <input 
                        className={`input ${errors.password ? 'is-danger' : ''}`}
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Enter your password"
                        autoComplete="new-password"
                      />
                      <span className="icon is-small is-left">
                        <i className="fas fa-lock"></i>
                      </span>
                    </div>
                    {errors.password && (
                      <p className="help is-danger">{errors.password}</p>
                    )}
                    <p className="psmall is-bold">Password must be at least 6 characters long</p>
                  </div>

                  <div className="field">
                    <label className="label">Confirm Password</label>
                    <div className="control has-icons-left">
                      <input 
                        className={`input ${errors.confirmPassword ? 'is-danger' : ''}`}
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder="Confirm your password"
                        autoComplete="new-password"
                      />
                      <span className="icon is-small is-left">
                        <i className="fas fa-lock"></i>
                      </span>
                    </div>
                    {errors.confirmPassword && (
                      <p className="help is-danger">{errors.confirmPassword}</p>
                    )}
                  </div>

                  <div className="field">
                    <div className="control">
                      <button 
                        className={`button is-primary is-fullwidth ${loading ? 'is-loading' : ''}`}
                        type="submit"
                        disabled={loading}
                      >
                        {isResetMode ? 'Reset Password' : 'Set Password'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SetPassword;